import { z } from 'zod';
import { RoleIdSchema } from './role.js';

// Users belong EITHER to a client (`client_id` set, role unset) OR to Omega
// staff (role set, client_id null). The role matrix sits on the role doc.
export const UserKindSchema = z.enum(['client', 'staff']);
export type UserKind = z.infer<typeof UserKindSchema>;

export const STAFF_OFFICES = ['London', 'Dubai', 'Indore', 'Remote'] as const;
export const StaffOfficeSchema = z.enum(STAFF_OFFICES);
export type StaffOffice = z.infer<typeof StaffOfficeSchema>;

export const USER_STATUS = ['invited', 'active', 'suspended', 'deactivated'] as const;
export const UserStatusSchema = z.enum(USER_STATUS);
export type UserStatus = z.infer<typeof UserStatusSchema>;

// Pragmatic email shape: local-part @ domain with a dotted TLD, no whitespace.
// No domain allowlist — any well-formed address is accepted.
const STAFF_EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// Single source of truth for staff-email validation — reused by the server
// (canonical, routes/users.ts) and the client modal (instant feedback).
export const StaffEmailSchema = z
  .string()
  .trim()
  .toLowerCase()
  .regex(STAFF_EMAIL_REGEX, 'Enter a valid email');

// Authorised-contact role (client side). Source: prototype/src/portal-extras.jsx step 2.
export const CLIENT_CONTACT_ROLES = [
  'Director',
  'CEO',
  'CFO',
  'General Counsel',
  'Treasurer',
  'Authorised representative',
] as const;
export const ClientContactRoleSchema = z.enum(CLIENT_CONTACT_ROLES);
export type ClientContactRole = z.infer<typeof ClientContactRoleSchema>;

export const UserSchema = z.object({
  id: z.string(),
  kind: UserKindSchema,

  email: z.string().email().toLowerCase(),
  name: z.string().min(1),
  initials: z.string().max(3).optional(),
  phone: z.string().optional(),

  // Staff
  role: RoleIdSchema.nullable().optional(),
  office: StaffOfficeSchema.nullable().optional(),

  // Client
  client_id: z.string().nullable().optional(),
  contact_role: ClientContactRoleSchema.nullable().optional(),

  status: UserStatusSchema.default('invited'),
  last_active_at: z.date().nullable().optional(),

  created_at: z.date(),
  updated_at: z.date(),
});
export type User = z.infer<typeof UserSchema>;

// Public-safe shape returned by GET /auth/me
export const SessionUserSchema = z.object({
  id: z.string(),
  kind: UserKindSchema,
  email: z.string().email(),
  name: z.string(),
  initials: z.string().optional(),
  phone: z.string().nullable().optional(),
  role: RoleIdSchema.nullable(),
  office: StaffOfficeSchema.nullable(),
  client_id: z.string().nullable(),
  contact_role: ClientContactRoleSchema.nullable(),
  status: UserStatusSchema,
});
export type SessionUser = z.infer<typeof SessionUserSchema>;
